import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface UseUnsavedChangesProps {
  hasUnsavedChanges: boolean;
  message?: string;
}

/**
 * Custom hook to handle beforeunload event when there are unsaved changes
 * Shows a confirmation dialog when user tries to leave the page with unsaved changes
 */
export const useUnsavedChanges = ({ 
  hasUnsavedChanges, 
  message 
}: UseUnsavedChangesProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        const confirmationMessage = message || t('settings.unsavedChangesWarning', 'You have unsaved changes. Are you sure you want to leave?');
        
        // Modern browsers require setting both returnValue and return
        event.preventDefault();
        event.returnValue = confirmationMessage;
        return confirmationMessage;
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, message, t]);
};
